import { useMemo, useState } from "react";
import EmptyState from "@/components/EmptyState";
import SearchInput from "@/components/SearchInput";
import Table, { TableControls, buildActionColumn } from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { useSearchWithParams } from "@/hooks/useSearchParams";
import { filterByClientSearch } from "@/lib/clientSearch";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { getStickyColumnClasses } from "@/lib/styleHelpers";
import TabLayout from "./_TabLayout";
import {
  ACHIEVEMENTS_FOR_MEMBER_QUERY,
  displayAchievementCodeTitle,
  type MembershipAchievementForMember
} from "@/pages/people/[pgaId]/queries";
import DeleteAchievementModal from "@/pages/people/[pgaId]/_components/DeleteAchievementModal";
import EditAchievementModal from "@/pages/people/[pgaId]/_components/EditAchievementModal";
import { usePersonEducationAccess } from "../person/_usePersonEducationAccess";

type CertificationTableRow = {
  achievement: MembershipAchievementForMember;
  certifiedOn: string;
  expiresOn: string;
  id: string;
  status: string | null;
  title: string;
};

type CertificationModalState = { mode: "add" } | { certification: MembershipAchievementForMember; mode: "edit" };

const formatTableData = (achievements: MembershipAchievementForMember[]): CertificationTableRow[] =>
  achievements.map((achievement) => ({
    id: achievement.id,
    achievement,
    title: achievement.title || "--",
    status: displayAchievementCodeTitle(achievement.code),
    certifiedOn: achievement.recognizedAt ? toFormat(achievement.recognizedAt) || "--" : "--",
    expiresOn: achievement.expiresOn ? toFormat(achievement.expiresOn) || "--" : "--"
  }));

export default function EducationCertifications() {
  const { pgaId } = useParams();
  const { loaded, canViewPersonEducation } = usePersonEducationAccess();
  const { Menu, setMenu } = useTableRowMenu();
  const [modalState, setModalState] = useState<CertificationModalState | null>(null);
  const [achievementToDelete, setAchievementToDelete] = useState<MembershipAchievementForMember | null>(null);
  const { data, loading, error } = useQuery(ACHIEVEMENTS_FOR_MEMBER_QUERY, {
    variables: { pgaId: pgaId!, achievement: "CERTIFICATION" },
    fetchPolicy: "cache-first",
    skip: !loaded || !canViewPersonEducation || !pgaId
  });

  const { searchValue, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });
  const hasSearch = !!searchValue.trim();
  const tableData = useMemo(
    () => (data?.achievementsForMember?.length ? formatTableData(data.achievementsForMember) : []),
    [data?.achievementsForMember]
  );
  const visibleRows = useMemo(
    () =>
      filterByClientSearch(tableData, searchValue, (row) => [row.title, row.status, row.certifiedOn, row.expiresOn]),
    [searchValue, tableData]
  );

  const columns = useMemo(
    () => [
      {
        header: "Title",
        id: "title",
        accessorKey: "title",
        size: 280,
        enableSorting: false,
        meta: {
          className: getStickyColumnClasses(!!tableData.length)
        }
      },
      {
        header: "Status",
        id: "status",
        accessorKey: "status",
        enableSorting: false
      },
      {
        header: "Certified On",
        id: "certifiedOn",
        accessorKey: "certifiedOn",
        enableSorting: false
      },
      {
        header: "Expires On",
        id: "expiresOn",
        accessorKey: "expiresOn",
        enableSorting: false
      },
      buildActionColumn<CertificationTableRow>({
        actionId,
        setMenu,
        getActions: (row): Action[] => {
          if (!row.achievement.canEdit) return [];

          return [
            {
              text: "Edit Certification",
              section: "Manage",
              onClick: () => setModalState({ mode: "edit", certification: row.achievement })
            },
            {
              text: "Delete Achievement",
              section: "Manage",
              iconName: "delete",
              destructive: true,
              onClick: () => setAchievementToDelete(row.achievement)
            }
          ];
        }
      })
    ],
    [setMenu, tableData.length]
  );

  if (!loaded) {
    return null;
  }

  if (error) return <div>Error: {error.message}</div>;

  return (
    <TabLayout>
      <div className="pt-12" />
      <Table
        loading={loading}
        data={visibleRows}
        columns={columns}
        hasActiveFilters={hasSearch}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={[
              {
                text: "Add Certification",
                iconName: "add",
                variant: "filled",
                onClick: () => setModalState({ mode: "add" }),
                testId: "add-certification-button"
              }
            ]}
            onSortClick={onSortClick}
          >
            <SearchInput
              placeholder="Search Certifications"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
          </TableControls>
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Certifications"
            caption="There are no certifications for this person."
            iconName="workspace_premium"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search." iconName="search" />
        )}
        data-testid="education-certifications-table"
      />
      <Menu />
      <EditAchievementModal
        open={modalState?.mode === "add" || modalState?.mode === "edit"}
        mode={modalState?.mode === "edit" ? "edit" : "add"}
        achievement={modalState?.mode === "edit" ? modalState.certification : null}
        category="CERTIFICATION"
        pgaId={pgaId!}
        onClose={() => setModalState(null)}
      />
      <DeleteAchievementModal
        open={!!achievementToDelete}
        achievement={achievementToDelete}
        category="CERTIFICATION"
        pgaId={pgaId!}
        onClose={() => setAchievementToDelete(null)}
      />
    </TabLayout>
  );
}
